/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(function () {
  var API_BASE = 'https://api.returnsdrive.com/services/';
  var GUARD_TOKEN = 'c8249db5787a10e40e67aa013412a0cc';
  var ORDER_KEY = 'order-origin-info';
  var STORE_KEY = 'storeId';
  var EMAIL_KEY = 'emailOrPhone';

  function apiHeaders() {
    return { 'shopify-returns-authorization': GUARD_TOKEN };
  }

  function resolveStoreId(override) {
    if (override) {
      return Promise.resolve(override);
    }

    var cached = sessionStorage.getItem(STORE_KEY);
    if (cached) {
      return Promise.resolve(cached);
    }

    var params = new URLSearchParams({ subdomain: window.location.hostname });
    if (window.Shopify && window.Shopify.shop) {
      params.set('storeId', window.Shopify.shop);
    }

    return fetch(API_BASE + 'store/detail?' + params, {
      headers: apiHeaders(),
      credentials: 'include',
    })
      .then(function (r) { return r.json(); })
      .then(function (payload) {
        var storeId = payload && payload.result && payload.result.storeId;
        if (!storeId) {
          throw new Error((payload && payload.message) || 'Returns store not found');
        }
        sessionStorage.setItem(STORE_KEY, storeId);
        console.log('[returns] resolved storeId:', storeId);
        return storeId;
      });
  }

  function lookupOrder(pairedOrder, emailOrPhone, storeId) {
    var params = new URLSearchParams({
      pairedOrder: pairedOrder,
      emailOrPhone: emailOrPhone,
      storeId: storeId,
      language: 'english',
    });

    return fetch(API_BASE + 'order/origin?' + params, {
      headers: apiHeaders(),
      credentials: 'include',
    })
      .then(function (r) {
        return r.json().then(function (payload) {
          if (!r.ok) {
            throw new Error((payload && payload.message) || 'order/origin failed (' + r.status + ')');
          }
          return payload.result || payload;
        });
      });
  }

  function primePortalSession(pairedOrder, emailOrPhone, storeId) {
    sessionStorage.setItem(
      ORDER_KEY,
      JSON.stringify({ pairedOrder: pairedOrder, emailOrPhone: emailOrPhone, storeId: storeId, language: 'english' })
    );
    sessionStorage.setItem(STORE_KEY, storeId);
    localStorage.setItem(EMAIL_KEY, emailOrPhone);
  }

  // Eagerly warm up storeId cache when the account page loads.
  // This runs in the background and has no visible side-effect.
  var _storeIdPromise = null;
  function warmStoreId() {
    if (!_storeIdPromise) {
      _storeIdPromise = resolveStoreId(null).catch(function (err) {
        console.warn('[returns] storeId warm-up failed:', err.message);
        _storeIdPromise = null;
      });
    }
    return _storeIdPromise;
  }

  // Fire the warm-up as soon as the script runs.
  warmStoreId();

  // Use capture: true so this fires BEFORE any bubble-phase stopPropagation
  // (the <td onclick="event.stopPropagation()"> wrapper would otherwise swallow the event).
  document.addEventListener('click', function (event) {
    var trigger = event.target.closest('.returns-portal-trigger');
    if (!trigger) {
      return;
    }

    // Stop both propagation and default navigation immediately (capture phase).
    event.stopPropagation();
    event.preventDefault();

    var pairedOrder = (trigger.dataset.pairedOrder || '').trim();
    var emailOrPhone = (trigger.dataset.emailOrPhone || '').trim();
    var portalBase = (trigger.dataset.returnsPortal || '/apps/returns').replace(/\/$/, '');
    var fallbackUrl = trigger.href;

    if (!pairedOrder || !emailOrPhone) {
      window.location.assign(fallbackUrl);
      return;
    }

    trigger.setAttribute('aria-busy', 'true');
    trigger.style.opacity = '0.6';
    trigger.style.pointerEvents = 'none';

    resolveStoreId(trigger.dataset.returnsStoreId || null)
      .then(function (storeId) {
        console.log('[returns] looking up order', pairedOrder, 'storeId', storeId);
        return lookupOrder(pairedOrder, emailOrPhone, storeId)
          .then(function (result) {
            primePortalSession(pairedOrder, emailOrPhone, storeId);

            var returnOrders = result.returnOrders || [];
            var dest = returnOrders.length > 0
              ? portalBase + '/history'
              : portalBase + '/request-action';

            console.log('[returns] navigating to', dest);
            window.location.assign(dest);
          });
      })
      .catch(function (err) {
        console.error('[returns] direct lookup failed, using pre-filled fallback:', err.message);
        window.location.assign(fallbackUrl);
      });
  }, true /* capture phase */);
})();

document.addEventListener('DOMContentLoaded', function() {
  const checkbox = document.getElementById('show-line-items');
  if (checkbox) {
    checkbox.addEventListener('change', function() {
      const lineItems = document.querySelectorAll('.line-items-column');
      lineItems.forEach(function(item) {
        if (checkbox.checked) {
          item.style.display = ''; // Or 'table-cell' for table elements if needed
        } else {
          item.style.display = 'none';
        }
      });
    });

    // We need to find the table cells specifically to set them to 'table-cell'
    const th = document.querySelector('th.line-items-column');
    const tds = document.querySelectorAll('td.line-items-column');

    checkbox.addEventListener('change', function() {
      const displayStyle = checkbox.checked ? 'table-cell' : 'none';
      if(th) {
        th.style.display = displayStyle;
      }
      tds.forEach(function(td) {
        td.style.display = displayStyle;
      });

      // For mobile view, we use block
      const mobileItems = document.querySelectorAll('.account__order-item-block.line-items-column');
      mobileItems.forEach(function(item) {
        item.style.display = checkbox.checked ? 'block' : 'none';
      });
    });
  }
});